"use client"

import { useEffect, useState } from "react"
import { businessHoursVerdict, qaOverrideAllowed } from "@/lib/business-hours"
import { useBusinessHoursSettings } from "./business-hours-provider"

/**
 * Read a QA override from sessionStorage (`"open"` / `"closed"`, set from
 * devtools). Returns `null` when not overridden or on a production host.
 */
function readOverride(): boolean | null {
  if (typeof window === "undefined") return null
  if (!qaOverrideAllowed(window.location.hostname)) return null
  try {
    const raw = sessionStorage.getItem("ahead-chat-force-hours")
    if (raw === "open") return true
    if (raw === "closed") return false
    return null
  } catch {
    return null
  }
}

/**
 * Client hook around `businessHoursVerdict()`. Returns `null` on the first
 * render (SSR + hydration) so callers can avoid a copy flash, then the
 * real boolean once the effect runs — or stays `null` when this browser
 * cannot evaluate the schedule, so the chat panel sends no `support_open`
 * and glue decides server-side. Re-checks every minute so a chat left open
 * across the hour boundary updates without a refresh.
 *
 * The `ahead-chat-force-hours` sessionStorage key (`"open"` / `"closed"`)
 * short-circuits the schedule for QA on non-production hosts.
 */
export function useBusinessHours(): boolean | null {
  const settings = useBusinessHoursSettings()
  const [open, setOpen] = useState<boolean | null>(null)

  useEffect(() => {
    function compute(): boolean | null {
      const override = readOverride()
      return override !== null ? override : businessHoursVerdict(settings)
    }
    setOpen(compute())
    const id = window.setInterval(() => setOpen(compute()), 60_000)
    return () => window.clearInterval(id)
  }, [settings])

  return open
}
